import {
    dinoAssistantUiIconAssets
} from "../assets/dino-assistant/dinoAssistantAssets.js";

import {
    openDinoAssistant
} from "../features/mascot/index.js";

import {
    useI18n
} from "../i18n/I18nProvider.js";

import {
    DinoAvatar
} from "../ui/components/DinoAvatar.js";

import {
    BackButton,
    Button,
    Card
} from "../ui/components/Controls.js";

import {
    Page,
    PageHeader
} from "../ui/components/Layout.js";

/**
 * Product transparency page for the contextual Dino assistant.
 *
 * Durable route:
 *
 * /info/dino
 */
function DinoAssistantPage() {
    const { t } = useI18n();

    const foundations = [
        {
            iconAsset: dinoAssistantUiIconAssets.context,
            title: t("institutional.dino.contextTitle"),
            body: t("institutional.dino.contextBody")
        },
        {
            iconAsset: dinoAssistantUiIconAssets.local,
            title: t("institutional.dino.localTitle"),
            body: t("institutional.dino.localBody")
        },
        {
            iconAsset: dinoAssistantUiIconAssets.speaker,
            title: t("institutional.dino.voiceTitle"),
            body: t("institutional.dino.voiceBody")
        }
    ];

    return (
        <Page>
            <BackButton fallback="/">
                ← {" "}{t("common.back")}
            </BackButton>

            <div className="mt-6 overflow-hidden rounded-card border border-dino-200 bg-[linear-gradient(135deg,var(--color-dino-50),white)] p-1 sm:p-2">
                <div className="rounded-[calc(var(--radius-card)-0.25rem)] bg-white/75 p-4 sm:p-6">
                    <PageHeader
                        eyebrow={t("institutional.dino.eyebrow")}
                        title={t("institutional.dino.title")}
                        description={t("institutional.dino.introduction")}
                        actions={
                            <Button
                                type="button"
                                onClick={() => openDinoAssistant()}
                            >
                                {t("institutional.dino.tryAction")}
                            </Button>
                        }
                    />
                </div>
            </div>

            <ol className="relative mt-6 grid gap-4 md:grid-cols-3">
                {foundations.map((item, index) => (
                    <li key={item.title} className="list-none">
                        <Card className="relative h-full overflow-hidden border-dino-200 p-5 shadow-sm sm:p-6">
                            <span
                                className="absolute end-4 top-3 text-4xl font-black text-dino-100"
                                aria-hidden="true"
                            >
                                {String(index + 1).padStart(2, "0")}
                            </span>
                            <span
                                className="grid size-12 place-items-center rounded-control border border-dino-200 bg-dino-50"
                                aria-hidden="true"
                            >
                                <img
                                    className="size-8"
                                    src={item.iconAsset}
                                    alt=""
                                />
                            </span>
                            <h2 className="mt-5 max-w-[14rem] text-lg font-extrabold text-ink">
                                {item.title}
                            </h2>
                            <p className="mt-3 text-sm leading-7 text-neutral-600">
                                {item.body}
                            </p>
                        </Card>
                    </li>
                ))}
            </ol>

            <section className="mt-6 overflow-hidden rounded-card border border-dino-300 bg-dino-950 text-white shadow-[0_1.2rem_3rem_rgb(3_39_31_/_0.16)]">
                <div className="grid gap-5 p-5 sm:grid-cols-[8rem_1fr] sm:items-center sm:p-7">
                    <div className="rounded-card bg-white/10 p-2">
                        <DinoAvatar
                            label={t("institutional.dino.avatarLabel")}
                            size={112}
                            state="thinking"
                        />
                    </div>
                    <div>
                        <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-dino-200">
                            {t("institutional.dino.nowEyebrow")}
                        </p>
                        <h2 className="mt-2 text-2xl font-extrabold text-white">
                            {t("institutional.dino.nowTitle")}
                        </h2>
                        <p className="mt-3 max-w-3xl text-sm leading-7 text-dino-50">
                            {t("institutional.dino.nowBody")}
                        </p>
                    </div>
                </div>

                <div className="grid border-t border-white/15 bg-white text-ink md:grid-cols-2">
                    <div className="p-5 sm:p-6">
                        <div className="flex items-start gap-3">
                            <img
                                className="size-9 shrink-0"
                                src={dinoAssistantUiIconAssets.check}
                                alt=""
                                aria-hidden="true"
                            />
                            <div>
                                <h3 className="font-extrabold text-ink">
                                    {t("institutional.dino.availableTitle")}
                                </h3>
                                <p className="mt-2 text-sm leading-7 text-neutral-600">
                                    {t("institutional.dino.availableBody")}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-dino-100 p-5 md:border-s md:border-t-0 sm:p-6">
                        <div className="flex items-start gap-3">
                            <img
                                className="size-9 shrink-0"
                                src={dinoAssistantUiIconAssets.planned}
                                alt=""
                                aria-hidden="true"
                            />
                            <div>
                                <h3 className="font-extrabold text-ink">
                                    {t("institutional.dino.plannedTitle")}
                                </h3>
                                <p className="mt-2 text-sm leading-7 text-neutral-600">
                                    {t("institutional.dino.plannedBody")}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <aside className="mt-6 flex items-start gap-4 rounded-card border border-warning-soft bg-warning-soft p-5 sm:p-6">
                <img
                    className="size-10 shrink-0"
                    src={dinoAssistantUiIconAssets.privacy}
                    alt=""
                    aria-hidden="true"
                />
                <div>
                    <h2 className="text-lg font-extrabold text-ink">
                        {t("institutional.dino.privacyTitle")}
                    </h2>
                    <p className="mt-2 text-sm leading-7 text-neutral-700">
                        {t("institutional.dino.privacyBody")}
                    </p>
                </div>
            </aside>
        </Page>
    );
}

export { DinoAssistantPage };
